/**
 * ============================================================
 * FILE: src/components/dev/ScrubAuditPanel.tsx
 * ============================================================
 * 
 * Dev-only floating panel that audits PHI scrubber output.
 * Shows before/after for every free-text field so Jamie can
 * verify the scrubber is doing the right thing.
 * 
 * ACTIVATION:
 *   - Keyboard: Ctrl+Shift+S (toggles panel)
 *   - URL param: ?scrub-debug=1 (auto-opens on load)
 *   - Only renders when import.meta.env.DEV is true
 * 
 * USAGE in NewCase.tsx:
 *   import { ScrubAuditPanel } from '../components/dev/ScrubAuditPanel';
 *   // At the bottom of the component, before closing </div>:
 *   {import.meta.env.DEV && <ScrubAuditPanel fields={scrubAuditFields} />}
 * 
 * Where scrubAuditFields is built from formData (see bottom of file for helper).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  scrubText,
  containsObviousPHI,
  checkScrubberHealth,
  type ScrubResult,
  type ScrubContext,
} from "../../services/scrubber";

// ============================================
// Types
// ============================================

export interface AuditField {
  name: string;
  label: string;
  value: string;
  source?: "form" | "denial" | "dynamic" | "strengthener";
}

interface FieldAuditResult {
  field: AuditField;
  result: ScrubResult | null;
  error: string | null;
  obviousPHI: boolean;
  duration_ms: number;
}

// ============================================
// Entity color map
// ============================================

const ENTITY_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  PERSON:       { bg: "#fef2f2", text: "#dc2626", label: "Name" },
  DATE_TIME:    { bg: "#fffbeb", text: "#d97706", label: "Date" },
  PHONE_NUMBER: { bg: "#f0fdf4", text: "#16a34a", label: "Phone" },
  EMAIL:        { bg: "#eff6ff", text: "#2563eb", label: "Email" },
  LOCATION:     { bg: "#faf5ff", text: "#9333ea", label: "Location" },
  SSN:          { bg: "#fef2f2", text: "#dc2626", label: "SSN" },
  MEDICAL_RECORD: { bg: "#fff7ed", text: "#ea580c", label: "MRN" },
  ID_NUMBER:    { bg: "#f0f9ff", text: "#0284c7", label: "ID" },
  DEFAULT:      { bg: "#f5f5f5", text: "#525252", label: "Other" },
};

function getEntityColor(type: string) {
  return ENTITY_COLORS[type] || ENTITY_COLORS.DEFAULT;
}

// ============================================
// Local PHI pattern detection (for offline mode)
// ============================================

interface LocalDetection {
  pattern: string;
  match: string;
  index: number;
}

function detectLocalPatterns(text: string): LocalDetection[] {
  const detections: LocalDetection[] = [];
  const patterns: [string, RegExp][] = [
    ["SSN", /\b\d{3}[-\s]?\d{2}[-\s]?\d{4}\b/g],
    ["Phone (US)", /\b(?:\+1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g],
    ["Email", /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/gi],
    ["DOB marker", /(?:DOB|Date\s*of\s*Birth)[:\s]*\d[^\n]{0,20}/gi],
    ["MRN", /MRN[:\s#]*[A-Z0-9]{6,}/gi],
    ["Member ID", /Member\s*ID[:\s#]*[A-Z0-9\-]{6,}/gi],
    ["Date (MM/DD/YYYY)", /\b(?:0[1-9]|1[0-2])[\/\-](?:0[1-9]|[12]\d|3[01])[\/\-](?:19|20)\d{2}\b/g],
    ["Name-like (Mr/Mrs/Dr)", /\b(?:Mr|Mrs|Ms|Dr|Prof)\.?\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?\b/g],
  ];

  for (const [label, regex] of patterns) {
    let m;
    // Reset lastIndex for each pattern
    regex.lastIndex = 0;
    while ((m = regex.exec(text)) !== null) {
      detections.push({ pattern: label, match: m[0], index: m.index });
    }
  }

  return detections.sort((a, b) => a.index - b.index);
}

// ============================================
// Highlighted text component
// ============================================

function HighlightedText({ text, entities }: { text: string; entities: { start: number; end: number; entity_type: string; score: number }[] }) {
  if (!entities.length) return <span className="text-xs text-slate-600 whitespace-pre-wrap break-all">{text}</span>;

  const sorted = [...entities].sort((a, b) => a.start - b.start);
  const parts: React.ReactNode[] = [];
  let lastEnd = 0;

  sorted.forEach((ent, i) => {
    if (ent.start > lastEnd) {
      parts.push(<span key={`t-${i}`} className="text-slate-600">{text.slice(lastEnd, ent.start)}</span>);
    }
    const color = getEntityColor(ent.entity_type);
    parts.push(
      <span
        key={`e-${i}`}
        style={{
          background: color.bg,
          color: color.text,
          borderBottom: `2px solid ${color.text}`,
          borderRadius: 2,
          padding: "0 2px",
        }}
        title={`${ent.entity_type} (${(ent.score * 100).toFixed(0)}%)`}
      >
        {text.slice(ent.start, ent.end)}
      </span>
    );
    lastEnd = ent.end;
  });

  if (lastEnd < text.length) {
    parts.push(<span key="t-end" className="text-slate-600">{text.slice(lastEnd)}</span>);
  }

  return <span className="text-xs whitespace-pre-wrap break-all">{parts}</span>;
}

// ============================================
// Field result card
// ============================================

function FieldResultCard({ audit }: { audit: FieldAuditResult }) {
  const [expanded, setExpanded] = useState(true);
  const { field, result, error, obviousPHI, duration_ms } = audit;

  const entityCount = result ? Object.values(result.entity_counts).reduce((a, b) => a + b, 0) : 0;
  const wasModified = result ? result.scrubbed_text !== field.value : false;
  const isClean = !error && !obviousPHI && !wasModified;

  return (
    <div style={{
      border: `1px solid ${error ? "#fecaca" : obviousPHI ? "#fed7aa" : wasModified ? "#bfdbfe" : "#e5e7eb"}`,
      borderRadius: 8,
      overflow: "hidden",
      marginBottom: 8,
    }}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          background: error ? "#fef2f2" : obviousPHI ? "#fff7ed" : wasModified ? "#eff6ff" : "#f9fafb",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{
            width: 7, height: 7, borderRadius: "50%",
            background: error ? "#dc2626" : obviousPHI ? "#ea580c" : wasModified ? "#2563eb" : "#16a34a",
            flexShrink: 0,
          }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: "#1f2937" }}>{field.label}</span>
          {field.source && (
            <span style={{
              fontSize: 10, padding: "1px 5px", borderRadius: 3,
              background: "#f3f4f6", color: "#6b7280",
            }}>{field.source}</span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {entityCount > 0 && (
            <span style={{ fontSize: 10, fontWeight: 600, color: "#2563eb", background: "#dbeafe", padding: "1px 5px", borderRadius: 3 }}>
              {entityCount} found
            </span>
          )}
          {isClean && (
            <span style={{ fontSize: 10, fontWeight: 600, color: "#16a34a", background: "#dcfce7", padding: "1px 5px", borderRadius: 3 }}>
              clean
            </span>
          )}
          {error && (
            <span style={{ fontSize: 10, fontWeight: 600, color: "#dc2626", background: "#fee2e2", padding: "1px 5px", borderRadius: 3 }}>
              error
            </span>
          )}
          <span style={{ fontSize: 10, color: "#9ca3af", fontFamily: "'DM Mono', monospace" }}>
            {duration_ms}ms
          </span>
          <span style={{ fontSize: 10, color: "#9ca3af", transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "0.15s" }}>▼</span>
        </div>
      </button>

      {expanded && (
        <div style={{ padding: 12, background: "white" }}>
          {/* Input text */}
          {field.value ? (
            <>
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: "#9ca3af", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Input ({field.value.length} chars)
                </div>
                <div style={{ padding: 8, borderRadius: 6, background: "#fafafa", border: "1px solid #f0f0f0", maxHeight: 120, overflow: "auto" }}>
                  {result?.entities?.length ? (
                    <HighlightedText text={field.value} entities={result.entities} />
                  ) : (
                    <span className="text-xs text-slate-600 whitespace-pre-wrap break-all">{field.value}</span>
                  )}
                </div>
              </div>

              {/* Scrubbed output */}
              {result && wasModified && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "#2563eb", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Scrubbed output
                  </div>
                  <div style={{ padding: 8, borderRadius: 6, background: "#f0f7ff", border: "1px solid #dbeafe", maxHeight: 120, overflow: "auto" }}>
                    <span className="text-xs whitespace-pre-wrap break-all" style={{ color: "#1e40af" }}>
                      {result.scrubbed_text}
                    </span>
                  </div>
                </div>
              )}

              {/* Entity breakdown */}
              {result && entityCount > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
                  {Object.entries(result.entity_counts).map(([type, count]) => {
                    const color = getEntityColor(type);
                    return (
                      <span key={type} style={{
                        fontSize: 10, padding: "2px 6px", borderRadius: 4,
                        background: color.bg, color: color.text, fontWeight: 600,
                      }}>
                        {color.label}: {count}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Local pattern check */}
              {obviousPHI && (
                <div style={{
                  padding: 8, borderRadius: 6,
                  background: "#fff7ed", border: "1px solid #fed7aa",
                  marginBottom: 8,
                }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "#ea580c", marginBottom: 4 }}>
                    ⚠ containsObviousPHI() = true
                  </div>
                  <div style={{ fontSize: 11, color: "#9a3412" }}>
                    Fallback pattern matcher would BLOCK this field if scrubber is down.
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div style={{ padding: 8, borderRadius: 6, background: "#fef2f2", border: "1px solid #fecaca" }}>
                  <span style={{ fontSize: 11, color: "#dc2626" }}>{error}</span>
                </div>
              )}

              {/* Metadata */}
              {result && (
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 10, color: "#9ca3af" }}>
                    version: <span style={{ fontFamily: "'DM Mono', monospace" }}>{result.version}</span>
                  </span>
                  {result.from_cache && (
                    <span style={{ fontSize: 10, color: "#16a34a", fontWeight: 600 }}>cached</span>
                  )}
                  {result.timings_ms?.total_ms != null && (
                    <span style={{ fontSize: 10, color: "#9ca3af" }}>
                      server: <span style={{ fontFamily: "'DM Mono', monospace" }}>{result.timings_ms.total_ms}ms</span>
                    </span>
                  )}
                </div>
              )}
            </>
          ) : (
            <span style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic" }}>Empty field — skipped</span>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// Local-only mode (no scrubber service needed)
// ============================================

function LocalPatternResults({ detections }: { detections: LocalDetection[] }) {
  if (!detections.length) {
    return (
      <div style={{ padding: 8, borderRadius: 6, background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
        <span style={{ fontSize: 11, color: "#16a34a", fontWeight: 600 }}>✓ No patterns detected by local fallback scanner</span>
      </div>
    );
  }
  return (
    <div style={{ padding: 8, borderRadius: 6, background: "#fff7ed", border: "1px solid #fed7aa" }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: "#ea580c", marginBottom: 6 }}>
        Local pattern detections ({detections.length}):
      </div>
      {detections.map((d, i) => (
        <div key={i} style={{ fontSize: 11, marginBottom: 2, display: "flex", gap: 6 }}>
          <span style={{ color: "#ea580c", fontWeight: 600, minWidth: 90 }}>{d.pattern}</span>
          <span style={{ color: "#9a3412", fontFamily: "'DM Mono', monospace", fontSize: 10 }}>"{d.match}"</span>
          <span style={{ color: "#d4d4d4", fontSize: 10 }}>@{d.index}</span>
        </div>
      ))}
    </div>
  );
}

// ============================================
// Free text tester
// ============================================

function FreeTextTester({ scrubberUp }: { scrubberUp: boolean | null }) {
  const [text, setText] = useState("");
  const [result, setResult] = useState<ScrubResult | null>(null);
  const [localDetections, setLocalDetections] = useState<LocalDetection[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runScrub = async () => {
    if (!text.trim()) return;
    setIsRunning(true);
    setError(null);
    setResult(null);

    // Always run local patterns
    setLocalDetections(detectLocalPatterns(text));

    if (scrubberUp) {
      try {
        const ctx: ScrubContext = { purpose: "llm", source: "user_input" };
        const r = await scrubText(text, ctx);
        setResult(r);
      } catch (e: unknown) {
        setError((e as Error).message);
      }
    }

    setIsRunning(false);
  };

  return (
    <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: "#9ca3af", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        Free text tester
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Paste any text to test scrubbing..."
        style={{
          width: "100%",
          height: 80,
          padding: 8,
          borderRadius: 6,
          border: "1px solid #e5e7eb",
          fontSize: 12,
          resize: "vertical",
          fontFamily: "'DM Sans', system-ui, sans-serif",
          outline: "none",
        }}
      />
      <div style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "center" }}>
        <button
          onClick={runScrub}
          disabled={!text.trim() || isRunning}
          style={{
            padding: "5px 12px",
            borderRadius: 6,
            border: "none",
            background: "#171717",
            color: "white",
            fontSize: 11,
            fontWeight: 600,
            cursor: text.trim() && !isRunning ? "pointer" : "not-allowed",
            opacity: text.trim() && !isRunning ? 1 : 0.4,
          }}
        >
          {isRunning ? "Scrubbing..." : "Test Scrub"}
        </button>
        <span style={{ fontSize: 10, color: "#9ca3af" }}>
          {text.length} chars
          {containsObviousPHI(text) && (
            <span style={{ color: "#ea580c", fontWeight: 600, marginLeft: 6 }}>⚠ obviousPHI=true</span>
          )}
        </span>
      </div>

      {/* Results */}
      {(result || localDetections.length > 0 || error) && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
          {error && (
            <div style={{ padding: 8, borderRadius: 6, background: "#fef2f2", border: "1px solid #fecaca", fontSize: 11, color: "#dc2626" }}>
              {error}
            </div>
          )}
          {result && result.scrubbed_text !== text && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#2563eb", marginBottom: 4 }}>SCRUBBED OUTPUT</div>
              <div style={{ padding: 8, borderRadius: 6, background: "#f0f7ff", border: "1px solid #dbeafe", fontSize: 12, color: "#1e40af", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                {result.scrubbed_text}
              </div>
              {Object.keys(result.entity_counts).length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                  {Object.entries(result.entity_counts).map(([type, count]) => {
                    const color = getEntityColor(type);
                    return (
                      <span key={type} style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: color.bg, color: color.text, fontWeight: 600 }}>
                        {color.label}: {count}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {result && result.scrubbed_text === text && (
            <div style={{ padding: 8, borderRadius: 6, background: "#f0fdf4", border: "1px solid #bbf7d0", fontSize: 11, color: "#16a34a", fontWeight: 600 }}>
              ✓ No PHI detected — text unchanged
            </div>
          )}
          <LocalPatternResults detections={localDetections} />
        </div>
      )}
    </div>
  );
}

// ============================================
// Main Panel Component
// ============================================

export function ScrubAuditPanel({ fields }: { fields: AuditField[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"form" | "test">("form");
  const [audits, setAudits] = useState<FieldAuditResult[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [scrubberUp, setScrubberUp] = useState<boolean | null>(null);
  const [lastScan, setLastScan] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Check scrubber health on mount
  useEffect(() => {
    checkScrubberHealth().then(setScrubberUp);
    const interval = setInterval(() => checkScrubberHealth().then(setScrubberUp), 30000);
    return () => clearInterval(interval);
  }, []);

  // Auto-open via URL param
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("scrub-debug") === "1") {
      setIsOpen(true);
    }
  }, []);

  // Keyboard shortcut: Ctrl+Shift+S
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "S") {
        e.preventDefault();
        setIsOpen((p) => !p);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Scan all fields
  const scanFields = useCallback(async () => {
    setIsScanning(true);
    setAudits([]);
    const results: FieldAuditResult[] = [];

    for (const field of fields) {
      if (!field.value?.trim()) {
        results.push({ field, result: null, error: null, obviousPHI: false, duration_ms: 0 });
        continue;
      }

      const start = performance.now();
      const obvious = containsObviousPHI(field.value);

      if (scrubberUp) {
        try {
          const ctx: ScrubContext = { purpose: "llm", source: field.source === "denial" ? "denial_letter" : "user_input" };
          const r = await scrubText(field.value, ctx);
          results.push({ field, result: r, error: null, obviousPHI: obvious, duration_ms: Math.round(performance.now() - start) });
        } catch (e: unknown) {
          results.push({ field, result: null, error: (e as Error).message, obviousPHI: obvious, duration_ms: Math.round(performance.now() - start) });
        }
      } else {
        // Offline mode — only local patterns
        results.push({ field, result: null, error: scrubberUp === false ? "Scrubber offline" : "Checking...", obviousPHI: obvious, duration_ms: Math.round(performance.now() - start) });
      }
    }

    setAudits(results);
    setLastScan(new Date().toLocaleTimeString());
    setIsScanning(false);
  }, [fields, scrubberUp]);

  // Don't render in production
  if (!import.meta.env.DEV) return null;

  // Floating trigger button (always visible)
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        title="Scrub Audit (Ctrl+Shift+S)"
        style={{
          position: "fixed", bottom: 16, right: 16, zIndex: 9999,
          width: 40, height: 40, borderRadius: 10,
          background: scrubberUp ? "#171717" : scrubberUp === false ? "#dc2626" : "#6b7280",
          border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          transition: "all 0.15s ease",
        }}
      >
        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
        </svg>
      </button>
    );
  }

  const nonEmptyFields = fields.filter((f) => f.value?.trim());
  const totalEntities = audits.reduce((sum, a) => {
    if (!a.result) return sum;
    return sum + Object.values(a.result.entity_counts).reduce((s, c) => s + c, 0);
  }, 0);

  return (
    <div
      ref={panelRef}
      style={{
        position: "fixed",
        bottom: 16,
        right: 16,
        width: 420,
        maxHeight: "calc(100vh - 32px)",
        zIndex: 9999,
        background: "white",
        borderRadius: 14,
        boxShadow: "0 8px 40px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        fontFamily: "'DM Sans', system-ui, sans-serif",
      }}
    >
      {/* Panel header */}
      <div style={{
        padding: "12px 16px",
        borderBottom: "1px solid #f0f0f0",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "#fafafa",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#171717" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
          </svg>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#171717" }}>Scrub Audit</span>
          {/* Status pill */}
          <span style={{
            fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 4,
            background: scrubberUp ? "#dcfce7" : scrubberUp === false ? "#fee2e2" : "#f3f4f6",
            color: scrubberUp ? "#16a34a" : scrubberUp === false ? "#dc2626" : "#6b7280",
          }}>
            {scrubberUp ? "● connected" : scrubberUp === false ? "● offline" : "● checking"}
          </span>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          style={{ background: "none", border: "none", cursor: "pointer", padding: 4, color: "#9ca3af" }}
          title="Close (Ctrl+Shift+S)"
        >
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: "1px solid #f0f0f0", flexShrink: 0 }}>
        {(["form", "test"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              flex: 1,
              padding: "8px 0",
              fontSize: 11,
              fontWeight: 600,
              color: activeTab === tab ? "#171717" : "#9ca3af",
              background: "none",
              border: "none",
              borderBottom: activeTab === tab ? "2px solid #171717" : "2px solid transparent",
              cursor: "pointer",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {tab === "form" ? `Scan Form (${nonEmptyFields.length})` : "Free Text"}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: "auto", padding: 12 }}>
        {activeTab === "form" ? (
          <>
            {/* Scan button */}
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
              <button
                onClick={scanFields}
                disabled={isScanning || nonEmptyFields.length === 0}
                style={{
                  padding: "6px 14px",
                  borderRadius: 7,
                  border: "none",
                  background: isScanning ? "#e5e7eb" : "#171717",
                  color: "white",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: isScanning || nonEmptyFields.length === 0 ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {isScanning ? (
                  <>
                    <span style={{ animation: "spin 1s linear infinite", display: "inline-block" }}>⟳</span>
                    Scanning...
                  </>
                ) : (
                  <>
                    <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                    </svg>
                    Scan All Fields
                  </>
                )}
              </button>
              {lastScan && (
                <span style={{ fontSize: 10, color: "#9ca3af" }}>
                  Last: {lastScan}
                  {totalEntities > 0 && (
                    <span style={{ marginLeft: 4, color: "#2563eb", fontWeight: 600 }}>{totalEntities} entities</span>
                  )}
                </span>
              )}
            </div>

            {/* Results */}
            {audits.length > 0 ? (
              audits.map((a, i) => <FieldResultCard key={i} audit={a} />)
            ) : (
              <div style={{ textAlign: "center", padding: "24px 0", color: "#9ca3af", fontSize: 12 }}>
                {nonEmptyFields.length === 0 ? (
                  <p>No fields have content yet. Fill out the form and scan.</p>
                ) : (
                  <p>Click "Scan All Fields" to audit {nonEmptyFields.length} field{nonEmptyFields.length > 1 ? "s" : ""}.</p>
                )}
              </div>
            )}
          </>
        ) : (
          <FreeTextTester scrubberUp={scrubberUp} />
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: "8px 12px",
        borderTop: "1px solid #f0f0f0",
        background: "#fafafa",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexShrink: 0,
      }}>
        <span style={{ fontSize: 10, color: "#d4d4d4" }}>
          DEV ONLY • Ctrl+Shift+S to toggle
        </span>
        <span style={{ fontSize: 10, color: "#d4d4d4", fontFamily: "'DM Mono', monospace" }}>
          {fields.length} fields tracked
        </span>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

// ============================================
// Helper: Build audit fields from NewCase formData
// ============================================

/**
 * Call this in NewCase.tsx to build the fields array:
 * 
 *   const scrubAuditFields = buildAuditFields(formData, {
 *     dynamicAnswers,
 *     strengthenerValues,
 *     denialPasteText: priorAuthDenialText,
 *   });
 * 
 * Then pass to: <ScrubAuditPanel fields={scrubAuditFields} />
 */
export function buildAuditFields(
  formData: Record<string, string>,
  extras?: {
    dynamicAnswers?: Record<string, string>;
    strengthenerValues?: Record<string, string>;
    denialPasteText?: string;
  }
): AuditField[] {
  const fields: AuditField[] = [];

  // Core free-text fields
  const coreFields: [string, string][] = [
    ["requestDetails", "Request Details"],
    ["additionalContext", "Clinical Context"],
    ["keyFacts", "Key Facts"],
    ["surgicalHistory", "Surgical History"],
    ["priorImagingFindings", "Prior Imaging Findings"],
    ["referralReason", "Referral Reason"],
  ];

  for (const [key, label] of coreFields) {
    if (formData[key]) {
      fields.push({ name: key, label, value: formData[key], source: "form" });
    }
  }

  // Dynamic answers
  if (extras?.dynamicAnswers) {
    Object.entries(extras.dynamicAnswers).forEach(([id, val]) => {
      if (val) {
        fields.push({ name: `dynamic_${id}`, label: `Dynamic: ${id}`, value: val, source: "dynamic" });
      }
    });
  }

  // Strengtheners
  if (extras?.strengthenerValues) {
    Object.entries(extras.strengthenerValues).forEach(([id, val]) => {
      if (val) {
        fields.push({ name: `strengthen_${id}`, label: `Strengthener: ${id}`, value: val, source: "strengthener" });
      }
    });
  }

  // Denial letter text
  if (extras?.denialPasteText) {
    fields.push({ name: "denialPasteText", label: "Denial Letter (pasted)", value: extras.denialPasteText, source: "denial" });
  }

  return fields;
}

export default ScrubAuditPanel;